import React from 'react';
import { makeStyles } from '@material-ui/core/styles';

import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../../constants';

const SCROLL = '@media (max-width: 640px)';

const useStyles = makeStyles(() => ({
    list: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        // On phones the row scrolls sideways instead of stacking.
        [SCROLL]: {
            flexWrap: 'nowrap',
            overflowX: 'auto',
            paddingBottom: '4px',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
                display: 'none',
            },
        },
    },
    tab: {
        flex: '0 0 auto',
        minHeight: '40px',
        padding: '0 17px',
        border: `1px solid ${REDESIGN_COLORS.navBorder}`,
        borderRadius: '999px',
        color: REDESIGN_COLORS.body,
        background: REDESIGN_COLORS.white,
        fontFamily: REDESIGN_FONTS.body,
        fontSize: '0.86rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        '&:hover': {
            borderColor: REDESIGN_COLORS.primary,
            color: REDESIGN_COLORS.link,
        },
    },
    tabOn: {
        borderColor: REDESIGN_COLORS.primary,
        color: REDESIGN_COLORS.white,
        background: REDESIGN_COLORS.primary,
        '&:hover': {
            color: REDESIGN_COLORS.white,
        },
    },
}));

/**
 * The pill tab strip of the "for any subject" card. Follows the ARIA tabs
 * pattern: arrow keys, Home and End move between tabs and select as they go.
 * Tab ids are `${idPrefix}-tab-${key}`; the caller labels its panel with them.
 */
const SubjectTabs = ({ items, value, onChange, ariaLabel, idPrefix, className }) => {
    const classes = useStyles();
    const buttons = React.useRef({});

    const select = (key) => {
        onChange(key);
        const button = buttons.current[key];
        if (button) button.focus();
    };

    const onKeyDown = (event) => {
        const index = items.findIndex((item) => item.key === value);
        let next = null;
        if (event.key === 'ArrowRight') next = (index + 1) % items.length;
        else if (event.key === 'ArrowLeft') next = (index - 1 + items.length) % items.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = items.length - 1;
        if (next === null) return;
        event.preventDefault();
        select(items[next].key);
    };

    return (
        <div
            role="tablist"
            aria-label={ariaLabel}
            onKeyDown={onKeyDown}
            className={[classes.list, className].filter(Boolean).join(' ')}
        >
            {items.map((item) => {
                const on = item.key === value;
                return (
                    <button
                        key={item.key}
                        ref={(el) => {
                            buttons.current[item.key] = el;
                        }}
                        type="button"
                        role="tab"
                        id={`${idPrefix}-tab-${item.key}`}
                        aria-controls={`${idPrefix}-panel-${item.key}`}
                        aria-selected={on}
                        tabIndex={on ? 0 : -1}
                        onClick={() => onChange(item.key)}
                        className={[classes.tab, on ? classes.tabOn : ''].filter(Boolean).join(' ')}
                    >
                        {item.label}
                    </button>
                );
            })}
        </div>
    );
};

export default SubjectTabs;
